const TASK_DOCUMENT_NAME_FALLBACK = 'Documento sin nombre';

const getDocumentsCollection = (payload) => {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.documents)) {
    return payload.documents;
  }

  if (Array.isArray(payload?.items)) {
    return payload.items;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  return [];
};

const normalizeBoolean = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  const normalized = String(value || '').trim().toLowerCase();
  return ['true', '1', 'si', 'yes'].includes(normalized);
};

const getDocumentName = (document) => {
  return document?.name
    || document?.title
    || document?.documentName
    || document?.displayName
    || document?.originalName
    || document?.originalname
    || document?.fileName
    || document?.filename
    || TASK_DOCUMENT_NAME_FALLBACK;
};

const getDocumentDownloadUrl = (document) => {
  return document?.downloadUrl
    || document?.fileUrl
    || document?.url
    || document?.href
    || null;
};

const getDocumentSize = (document) => {
  const rawValue = document?.size ?? document?.fileSize ?? document?.bytes ?? null;
  const parsedValue = Number(rawValue);

  if (!Number.isFinite(parsedValue) || parsedValue < 0) {
    return null;
  }

  return parsedValue;
};

const getDocumentCreatedAt = (document) => {
  return document?.createdAt
    || document?.uploadedAt
    || document?.date
    || document?.updatedAt
    || null;
};

const getDocumentMimeType = (document) => {
  return document?.mimeType
    || document?.contentType
    || document?.type
    || '';
};

const getDocumentExtension = (documentName) => {
  const fileName = String(documentName || '').trim();
  const lastDotIndex = fileName.lastIndexOf('.');

  if (lastDotIndex <= 0 || lastDotIndex === fileName.length - 1) {
    return '';
  }

  return fileName.slice(lastDotIndex + 1).toUpperCase();
};

const getVisibleToTechnician = (document) => {
  return normalizeBoolean(
    document?.visibleToTechnician
    ?? document?.technicianVisible
    ?? document?.isVisibleToTechnician
    ?? document?.forTechnician
    ?? document?.technicianCanView
    ?? document?.sharedWithTechnician
  );
};

const parseJsonSafely = async (response) => {
  try {
    return await response.json();
  } catch {
    return null;
  }
};

const buildResponseError = async (response, fallbackMessage) => {
  const payload = await parseJsonSafely(response);
  const error = new Error(payload?.message || `${fallbackMessage} Codigo: ${response.status}`);
  error.status = response.status;
  error.payload = payload;
  return error;
};

const getDocumentPayload = (payload) => {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }

  if (payload.document && typeof payload.document === 'object' && !Array.isArray(payload.document)) {
    return payload.document;
  }

  return payload;
};

export const normalizeTaskDocument = (document) => {
  const name = getDocumentName(document);

  return {
    ...document,
    id: document?.id || document?._id || document?.documentId || name,
    name,
    createdAt: getDocumentCreatedAt(document),
    downloadUrl: getDocumentDownloadUrl(document),
    mimeType: getDocumentMimeType(document),
    size: getDocumentSize(document),
    extension: getDocumentExtension(name),
    visibleToTechnician: getVisibleToTechnician(document)
  };
};

export const fetchTaskDocuments = async (taskId) => {
  const response = await fetch(`/api/tasks/${taskId}/documents`, {
    method: 'GET'
  });

  if (!response.ok) {
    throw await buildResponseError(response, 'No se pudieron obtener los documentos.');
  }

  const payload = await parseJsonSafely(response);
  return getDocumentsCollection(payload).map(normalizeTaskDocument);
};

export const uploadTaskDocument = async (taskId, { file, name, visibleToTechnician }) => {
  const formData = new FormData();
  formData.append('file', file);

  if (name?.trim()) {
    formData.append('name', name.trim());
  }

  formData.append('visibleToTechnician', String(Boolean(visibleToTechnician)));

  const response = await fetch(`/api/tasks/${taskId}/documents`, {
    method: 'POST',
    body: formData
  });

  if (!response.ok) {
    throw await buildResponseError(response, 'No se pudo subir el documento.');
  }

  const payload = await parseJsonSafely(response);
  const documentPayload = getDocumentPayload(payload);
  return documentPayload ? normalizeTaskDocument(documentPayload) : null;
};

export const updateTaskDocument = async (taskId, document, updates = {}) => {
  const documentId = document?.id;
  const payload = {
    visibleToTechnician: Boolean(updates.visibleToTechnician)
  };

  const response = await fetch(`/api/tasks/${taskId}/documents/${documentId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    throw await buildResponseError(response, 'No se pudo actualizar el documento.');
  }

  const responsePayload = await parseJsonSafely(response);
  const updatedDocument = getDocumentPayload(responsePayload);

  return normalizeTaskDocument(updatedDocument || {
    ...document,
    ...payload
  });
};

export const deleteTaskDocument = async (taskId, document) => {
  const documentId = document?.id;
  const response = await fetch(`/api/tasks/${taskId}/documents/${documentId}`, {
    method: 'DELETE'
  });

  if (!response.ok) {
    throw await buildResponseError(response, 'No se pudo eliminar el documento.');
  }
};

const extractFileNameFromDisposition = (contentDispositionValue) => {
  if (!contentDispositionValue) {
    return '';
  }

  const utf8Match = contentDispositionValue.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]);
  }

  const asciiMatch = contentDispositionValue.match(/filename="?([^"]+)"?/i);
  return asciiMatch?.[1] || '';
};

export const fetchTaskDocumentBlob = async (taskId, document) => {
  const downloadUrl = document?.downloadUrl || `/api/tasks/${taskId}/documents/${document.id}/download`;
  const response = await fetch(downloadUrl, {
    method: 'GET'
  });

  if (!response.ok) {
    throw new Error(`No se pudo descargar el documento. Codigo: ${response.status}`);
  }

  const blob = await response.blob();
  const fileName = extractFileNameFromDisposition(response.headers.get('content-disposition'))
    || document?.name
    || TASK_DOCUMENT_NAME_FALLBACK;

  return {
    blob,
    fileName
  };
};

export const downloadTaskDocument = async (taskId, document) => {
  return fetchTaskDocumentBlob(taskId, document);
};
